import React, { useEffect, useState } from "react";
import PictureTypes from "api/models/PictureTypes";
import { ImageItem } from "hooks/helpers/Interfaces";
import {
  getSlides,
  Ratio,
} from "components/forms/FormFields/PictureGallery/helpers";
import APIErrorResponse from "common/api/models/APIErrorResponse";
import OverlaySpinner from "components/generic/OverlaySpinner";
import CropAndZoomDialog from "components/generic/CropAndZoomDialog";
import FieldErrors from "components/forms/FieldErrors";
import FixedSinglePicture from "components/forms/FormFields/SinglePicture/FixedSinglePicture";

interface Props {
  width?: string;
  height?: string;
  container: string;
  parseErrors?: (response: APIErrorResponse) => void;
  updateKey?: string;
  errors?: string[];
  setImageUrl?: (imageUrl: string | null) => void;
  imageUrl?: string | null;
  withCropAndZoom?: boolean;
  ratio: Ratio;
}

const SinglePicture = ({
  errors,
  imageUrl,
  width,
  height,
  container,
  parseErrors,
  updateKey,
  setImageUrl,
  withCropAndZoom,
  ratio,
}: Props) => {
  const [picture, setPicture] = useState<ImageItem>(getSlides(null, 1)[0]);
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [showCropAndZoom, setShowCropAndZoom] = useState<boolean>(false);
  const [cropAndZoomImageItem, setCropAndZoomImageItem] = useState<ImageItem>();
  const [cropAndZoomImageUrl, setCropAndZoomImageUrl] = useState<string>("");
  const [cropAndZoomImageType, setCropAndZoomImageType] = useState<string>("");
  const [cropAndZoomImageFileName, setCropAndZoomImageFileName] = useState<
    string
  >("");
  const [cropAndZoomRatio, setCropAndZoomRatio] = useState<number>(ratio);
  const [cropAndZoomMinZoom, setCropAndZoomMinZoom] = useState<number>(1);
  const [cropAndZoomMaxZoom, setCropAndZoomMaxZoom] = useState<number>(3);
  const [cropAndZoomCropShape, setCropAndZoomCropShape] = useState<
    "rect" | "round"
  >("round");
  const [cropAndZoomContainer, setCropAndZoomContainer] = useState<string>("");
  const [cropAndZoomWarning, setCropAndZoomWarning] = useState<boolean>(false);
  const [cropAndZoomPortraitWarning, setCropAndZoomPortraitWarning] = useState<
    boolean
  >(false);

  const initSlides = (): void => {
    setPicture(
      getSlides(
        [
          {
            url: imageUrl,
            type: PictureTypes.main,
          },
        ],
        1,
      )[0],
    );
  };

  useEffect(() => {
    if (imageUrl && imageUrl !== picture.url) {
      initSlides();
    }
    // TODO: Fix to match eslint rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl]);

  useEffect(() => {
    if (imageUrl) {
      initSlides();
    }
    // TODO: Fix to match eslint rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateKey]);

  const removePicture = () => {
    setPicture(getSlides(null, 1)[0]);
  };

  const updateSlide = (image: ImageItem) => {
    if (setImageUrl) setImageUrl(image.url || null);
  };

  const [slideErrors, setSlideErrors] = useState<string[]>([]);

  const onInitialUpload = (
    sourceImageUrl: string,
    ratio: number,
    cropShape: "rect" | "round",
    containerToUse: string,
  ) => {
    const image = new Image();
    image.src = sourceImageUrl;
    image.addEventListener("load", () => {
      const imageRatio = image.naturalWidth / image.naturalHeight;
      const minZoom = Math.min(
        imageRatio < ratio
          ? (1 / ratio) * imageRatio
          : ratio * (1 / imageRatio),
        1,
      );
      const maxZoom = minZoom + 2;

      setCropAndZoomImageUrl(sourceImageUrl);
      setCropAndZoomRatio(ratio);
      setCropAndZoomMinZoom(minZoom);
      setCropAndZoomMaxZoom(maxZoom);
      setCropAndZoomCropShape(cropShape);
      setCropAndZoomContainer(containerToUse);
      setCropAndZoomWarning(image.naturalWidth < 720);
      setCropAndZoomPortraitWarning(image.naturalWidth < image.naturalHeight);

      setShowCropAndZoom(true);
    });
  };

  const onHideCropAndZoom = (imageItem: ImageItem, sourceImageUrl: string) => {
    setShowCropAndZoom(false);

    const newImageItem: ImageItem = {
      type: imageItem.type,
      url: sourceImageUrl,
    };

    updateSlide(newImageItem);
  };

  return (
    <>
      <FixedSinglePicture
        updateSlide={updateSlide}
        picture={picture}
        removePicture={removePicture}
        height={height}
        width={width}
        container={container}
        parseErrors={parseErrors}
        setWaitingStatus={setShowOverlay}
        hasFormErrors={Boolean(errors && errors.length > 0)}
        slideErrors={slideErrors}
        setSlideErrors={setSlideErrors}
        onInitialUpload={(sourceImageUrl: string) => {
          setCropAndZoomImageItem(picture);
          onInitialUpload(sourceImageUrl, ratio, "rect", container);
        }}
        setImageType={setCropAndZoomImageType}
        setImageFileName={setCropAndZoomImageFileName}
        ratio={ratio}
      />
      <FieldErrors errors={errors} />
      {showOverlay && <OverlaySpinner />}
      {withCropAndZoom && (
        <CropAndZoomDialog
          isVisible={showCropAndZoom}
          onClose={onHideCropAndZoom}
          imageItem={cropAndZoomImageItem}
          imageUrl={cropAndZoomImageUrl}
          setImageUrl={setCropAndZoomImageUrl}
          imageType={cropAndZoomImageType}
          imageFileName={cropAndZoomImageFileName}
          ratio={cropAndZoomRatio}
          minZoom={cropAndZoomMinZoom}
          maxZoom={cropAndZoomMaxZoom}
          cropShape={cropAndZoomCropShape}
          parseErrors={parseErrors}
          container={cropAndZoomContainer}
          setSlideErrors={setSlideErrors}
          showWarning={cropAndZoomWarning}
          showPortraitWarning={cropAndZoomPortraitWarning}
        />
      )}
    </>
  );
};

export default SinglePicture;
